import { useId, type ComponentProps, type ReactNode } from "react";
import { PiCheckBold } from "react-icons/pi";
import { cn } from "@steelyard/theme";

/**
 * Every form checkbox in apps/admin — a status (Active), a switch (Allow piece purchase): a small
 * checkbox inside a bordered box, the WHOLE box one `<label>` so a click anywhere on it toggles,
 * the title and its description stacked beside the check, top-aligned with the title's first line.
 * Unchecked it is a quiet grey card; checked, its border, a full ring and a faint tint all turn the
 * brand colour, so the two states differ at a glance, not by a shade. A keyboard focus ring and a
 * dimmed disabled state are the box's own (2026-09-28, the project owner's request).
 *
 * The card treatment — a ring that strengthens on hover and on checked — is the template's
 * selectable card, COPIED from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/multi-step/multi-step-1/step-6.tsx's
 * `AdvancedCheckbox`, with the check itself kept visible. The check is the platform's own checkbox
 * (styled by @tailwindcss/forms, 16px — the Marketplace admin portal's size), so it works with
 * `{...register("isActive")}` and with `checked`/`onChange` alike, and a surrounding
 * `<fieldset disabled>` disables it too.
 */
export function CheckboxCard({
  label,
  helperText,
  className,
  id: idOverride,
  ...input
}: {
  label: ReactNode;
  helperText?: ReactNode;
  className?: string;
} & Omit<ComponentProps<"input">, "type">) {
  const generated = useId();
  const id = idOverride ?? generated;

  return (
    <label
      htmlFor={id}
      className={cn(
        "flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 bg-gray-0 p-4 transition-colors dark:bg-gray-50",
        "hover:border-gray-300 hover:bg-gray-50/70",
        "has-[:checked]:border-primary has-[:checked]:bg-primary/[0.04] has-[:checked]:ring-1 has-[:checked]:ring-primary",
        "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary/40",
        "has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60 has-[:disabled]:hover:bg-gray-0",
        className,
      )}
    >
      {/* The tick is drawn over the box, as rizzui's own Checkbox does: the template's stylesheet
          clears the forms plugin's checkmark image from a checked box. */}
      <span className="relative mt-0.5 flex size-4 shrink-0">
        <input
          id={id}
          type="checkbox"
          className="peer size-4 cursor-pointer rounded border-gray-300 text-primary focus:ring-0 focus:ring-offset-0 disabled:cursor-not-allowed"
          {...input}
        />
        <PiCheckBold aria-hidden className="pointer-events-none absolute inset-0 m-auto size-3 text-white opacity-0 peer-checked:opacity-100" />
      </span>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-sm font-medium leading-5 text-gray-900">{label}</span>
        {helperText ? <span className="text-[13px] leading-snug text-gray-500">{helperText}</span> : null}
      </span>
    </label>
  );
}
